import React, { useState, useMemo } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Table, Button, Form, Spinner, Badge, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import jsPDF from "jspdf";
import logo from "../assets/img/logo.png";

const API = process.env.REACT_APP_API;

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');
const StudentAvatar = ({ photo, name, size = 28 }) => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initial = name ? name.trim().charAt(0) : '?';
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null;

  const circleStyle = { width: size, height: size, borderRadius: '50%', flexShrink: 0 };
  return (
    <>
      <span
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        onMouseEnter={() => src && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => src && setShowModal(true)}
      >
        {src
          ? <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover', cursor: 'pointer' }} />
          : <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 600 }}>{initial}</span>
        }
        {src && hovered && (
          <div style={{ position: 'absolute', left: size + 6, top: '50%', transform: 'translateY(-50%)', zIndex: 1050, background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
          </div>
        )}
      </span>
      {src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center">
            <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
            {name && <div className="mt-2 fw-semibold small">{name}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

// วิชาที่มีผลการเรียนหรือไม่ (มี g_id และ g_grade เป็นตัวเลข)
const hasGrade = (r) =>
  r.g_id !== null && r.g_id !== undefined &&
  r.g_grade !== null && r.g_grade !== undefined && r.g_grade !== '';

// วิชาที่ต้องสอบแก้ตัว: ติด ร, ติด 0 (คะแนน < 50) หรือยังไม่มีผลการเรียน (ค่าว่าง)
// — กล่าวคือ ทุกวิชาที่ยังไม่ผ่าน (ไม่นับเฉพาะวิชาที่ได้คะแนน >= 50)
const isMakeup = (r) =>
  String(r.g_waiting) === '1' || !hasGrade(r) || parseFloat(r.g_grade) < 50;

// ผลการสอบเดิมที่จะแสดง: "ร" สำหรับติด ร, "0" สำหรับติด 0, "" สำหรับยังไม่มีผล (ค่าว่าง)
const originalResult = (r) => {
  if (String(r.g_waiting) === '1') return 'ร';
  if (!hasGrade(r)) return '';
  return '0';
};

// index ของภาคเรียน เพื่อนับจำนวนภาคเรียนที่ผ่านไป
const termIndex = (yearYear, semester) =>
  parseInt(yearYear, 10) * 2 + (parseInt(semester, 10) - 1);

// ค่าธรรมเนียมต่อวิชาตามจำนวนภาคเรียนที่ผ่านไป
const feeForElapsed = (elapsed, nonSubsidy) => {
  if (nonSubsidy) return 120;
  if (elapsed <= 1) return 20;
  if (elapsed === 2) return 40;
  return 50;
};

// ภาคเรียนปัจจุบันโดยประมาณจากวันที่ (พ.ค.–ก.ย. = เทอม 1, ที่เหลือ = เทอม 2)
const computeCurrentTerm = () => {
  const now = new Date();
  const buddhistYear = String(now.getFullYear() + 543);
  const month = now.getMonth() + 1;
  const semester = month >= 5 && month <= 9 ? '1' : '2';
  return { year_year: buddhistYear, semester };
};

// วันที่ปัจจุบันแบบไทย เช่น "28 มิถุนายน 2569"
const THAI_MONTHS = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
const formatThaiDate = (d = new Date()) =>
  `${d.getDate()} ${THAI_MONTHS[d.getMonth()]} ${d.getFullYear() + 543}`;

const formatCredit = (n) => {
  const v = parseFloat(n);
  if (isNaN(v)) return '-';
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
};

export default function MakeupExamRequest() {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const [searchId, setSearchId]   = useState('');
  const [result, setResult]       = useState(null);   // { student, rows }
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound]   = useState(false);

  const [selectedTermKey, setSelectedTermKey] = useState('');   // "year_id|semester" ของเทอมที่ติด
  const [nonSubsidy, setNonSubsidy]           = useState(false);
  const [selectedKeys, setSelectedKeys]       = useState(() => new Set()); // วิชาที่ติ๊กเลือกจะสอบแก้

  const rowKey = (r) => `${r.teach_id}-${r.sub_id}`;

  // ภาคเรียนปัจจุบัน คำนวณจากวันที่ปัจจุบันอัตโนมัติ (ล็อกไว้ ไม่ให้เลือก)
  const currentTerm = computeCurrentTerm();

  const handleSearch = () => {
    if (!searchId.trim()) return;
    setSearching(true);
    setResult(null);
    setNotFound(false);
    setSelectedTermKey('');
    axiosInstance.get(`${API}/getStudentGradesByIdNumber/${searchId.trim()}`, {
      headers: { authorization: `Bearer ${getToken()}` }
    })
    .then(res => setResult(res.data))
    .catch(err => {
      if (err.response?.status === 404) setNotFound(true);
    })
    .finally(() => setSearching(false));
  };

  // เทอมที่มีวิชาติด 0 / ร ให้เลือก
  const failedTerms = useMemo(() => {
    if (!result?.rows) return [];
    const map = new Map();
    result.rows.filter(isMakeup).forEach(r => {
      const key = `${r.year_id}|${r.sub_semester}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          year_id: r.year_id,
          year_year: r.year_year,
          semester: String(r.sub_semester),
          c_class: r.c_class,
          c_id: r.c_id,
          r_no: r.r_no,
          rows: [],
        });
      }
      map.get(key).rows.push(r);
    });
    return Array.from(map.values()).sort((a, b) =>
      termIndex(b.year_year, b.semester) - termIndex(a.year_year, a.semester));
  }, [result]);

  // ตั้งค่าเริ่มต้นเมื่อได้ผลค้นหาใหม่
  React.useEffect(() => {
    if (failedTerms.length > 0) setSelectedTermKey(failedTerms[0].key);
    setNonSubsidy(false);
  }, [failedTerms]);

  const selectedTerm = failedTerms.find(t => t.key === selectedTermKey) || null;
  const subjects = selectedTerm ? selectedTerm.rows : [];

  // เมื่อเปลี่ยนเทอม ให้ติ๊กเลือกทุกวิชาเป็นค่าเริ่มต้น
  React.useEffect(() => {
    const t = failedTerms.find(x => x.key === selectedTermKey);
    setSelectedKeys(new Set((t?.rows || []).map(r => `${r.teach_id}-${r.sub_id}`)));
  }, [selectedTermKey, failedTerms]);

  const toggleKey = (k) => setSelectedKeys(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const selectAll = () => setSelectedKeys(new Set(subjects.map(rowKey)));
  const clearAll  = () => setSelectedKeys(new Set());

  // วิชาที่ถูกเลือกจริง — ใช้คิดค่าธรรมเนียมและพิมพ์ลงฟอร์ม
  const chosen = subjects.filter(r => selectedKeys.has(rowKey(r)));

  const elapsed = useMemo(() => {
    if (!selectedTerm) return 1;
    const diff = termIndex(currentTerm.year_year, currentTerm.semester)
      - termIndex(selectedTerm.year_year, selectedTerm.semester);
    return Math.max(1, diff);
  }, [selectedTerm, currentTerm.year_year, currentTerm.semester]);

  const feePerSubject = feeForElapsed(elapsed, nonSubsidy);
  const totalMoney = chosen.length * feePerSubject;

  // ---------- พิมพ์ / PDF ----------
  const PRINT_STYLES = `
    * { box-sizing: border-box; }
    body { font-family: 'Sarabun', 'TH Sarabun New', Arial, sans-serif; font-size: 14px; color: #000; margin: 0; }
    .wrap { padding: 6px 4px; }
    .head { text-align: center; }
    .head img { width: 70px; height: auto; }
    .head .school { font-size: 18px; font-weight: bold; margin-top: 2px; }
    .head .title { font-size: 16px; margin-top: 2px; }
    .info { margin: 10px 0 6px; font-size: 14px; line-height: 1.9; }
    .dotted { border-bottom: 1px dotted #000; display: inline-block; min-width: 120px; padding: 0 4px; text-align: center; }
    .dotted.sm { min-width: 55px; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #000; padding: 3px 5px; font-size: 13px; }
    th { text-align: center; }
    .sec { text-align: left; font-weight: bold; background: #f0f0f0; }
    .c { text-align: center; }
    .blank { height: 24px; }
    .totals { margin-top: 10px; font-size: 14px; display: flex; justify-content: center; gap: 48px; }
    .note { margin-top: 8px; font-size: 12px; }
    .expiry { margin-top: 6px; font-size: 13px; font-weight: bold; color: #c00; }
    .sign { margin-top: 36px; text-align: center; font-size: 14px; line-height: 2.4; }
    .sign .line { min-width: 180px; }
    .sign .date { min-width: 140px; }
    @media print { @page { size: A4; margin: 12mm; } }
  `;

  const escapeHtml = (s) => String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

  const buildRowsHtml = () => {
    const basic = chosen.filter(r => r.sub_type !== 'extra');
    const extra = chosen.filter(r => r.sub_type === 'extra');
    const renderGroup = (label, list) => {
      if (list.length === 0) return '';
      let html = `<tr><td class="sec" colspan="8">${label}</td></tr>`;
      html += list.map(r => `
        <tr>
          <td class="c">${escapeHtml(r.sub_subid)}</td>
          <td>${escapeHtml(r.sub_subname)}</td>
          <td class="c">${formatCredit(r.sub_credit)}</td>
          <td class="c">${originalResult(r)}</td>
          <td class="blank"></td>
          <td>${escapeHtml(r.teacher_name)}</td>
          <td class="blank"></td>
          <td></td>
        </tr>`).join('');
      return html;
    };
    return renderGroup('สาระการเรียนรู้พื้นฐาน', basic) + renderGroup('สาระการเรียนรู้เพิ่มเติม', extra);
  };

  const buildFormHtml = () => {
    if (!result || !selectedTerm) return '';
    const st = result.student;
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + 7);
    return `
      <div class="wrap">
        <div class="head">
          <img src="${logo}" alt="logo" />
          <div class="school">โรงเรียนวัฒนธรรมอิสลาม</div>
          <div class="title">แบบคำร้องสอบแก้ตัว ชั้น${escapeHtml(selectedTerm.c_class)}</div>
        </div>
        <div class="info">
          ชื่อ <span class="dotted">${escapeHtml(st.st_name)}</span>
          เลขประจำตัว <span class="dotted">${escapeHtml(st.st_idnumber)}</span>
          ชั้น <span class="dotted">ม. ${escapeHtml(selectedTerm.c_id)} / ${escapeHtml(selectedTerm.r_no)}</span><br/>
          มีความประสงค์ขอสอบแก้ตัวในภาคเรียนที่ <span class="dotted">${escapeHtml(selectedTerm.semester)}</span>
          ปีการศึกษา <span class="dotted">${escapeHtml(selectedTerm.year_year)}</span>
          ในรายวิชาต่อไปนี้
        </div>
        <table>
          <thead>
            <tr>
              <th style="width:70px;">รหัสวิชา</th>
              <th>รายวิชา</th>
              <th style="width:45px;">นก.</th>
              <th style="width:75px;">ผลการสอบเดิม</th>
              <th style="width:75px;">ผลการสอบแก้</th>
              <th style="width:130px;">ครูผู้สอน</th>
              <th style="width:90px;">วัน เดือน ปี</th>
              <th style="width:90px;">หมายเหตุ</th>
            </tr>
          </thead>
          <tbody>${buildRowsHtml()}</tbody>
        </table>
        <div class="totals">
          <div>รวม <span class="dotted sm">${chosen.length}</span> วิชา</div>
          <div>รวมเงิน <span class="dotted sm">${totalMoney}</span> บาท</div>
        </div>
        <div class="note">
          หมายเหตุ ห้ามใช้น้ำยาลบคำผิดในการแก้ไข หากมีการแก้ไขให้ขีดด้วยปากกาสีแดง
          พร้อมลงชื่อครูประจำรายวิชา/ครูประจำชั้นเซ็นต์กำกับเท่านั้น
        </div>
        <div class="expiry">
          ใบคำร้องนี้มีอายุ 7 วัน (ออกให้วันที่ ${escapeHtml(formatThaiDate())} ใช้ได้จนถึงวันที่ ${escapeHtml(formatThaiDate(expiry))})
        </div>
        <div class="sign">
          <div>ลงชื่อ <span class="dotted line"></span> (งานวัดผล และประเมินผล)</div>
          <div>วันที่ <span class="dotted date">${escapeHtml(formatThaiDate())}</span></div>
        </div>
      </div>`;
  };

  const handlePrint = () => {
    if (!selectedTerm || chosen.length === 0) return;
    const win = window.open('', '_blank', 'width=900,height=1000');
    win.document.write(`
      <html><head><title>คำร้องสอบแก้ตัว ${result.student.st_idnumber}</title>
      <meta charset="utf-8" />
      <style>${PRINT_STYLES}</style>
      </head><body onload="setTimeout(function(){window.print();},300)">${buildFormHtml()}</body></html>
    `);
    win.document.close();
  };

  const handleExportPDF = async () => {
    if (!selectedTerm || chosen.length === 0) return;
    const html2canvas = (await import('html2canvas')).default;
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:fixed;left:-9999px;top:0;background:#fff;width:794px;';
    wrapper.innerHTML = `<style>${PRINT_STYLES}</style>${buildFormHtml()}`;
    document.body.appendChild(wrapper);
    const canvas = await html2canvas(wrapper, { scale: 2, useCORS: true });
    document.body.removeChild(wrapper);
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgW = pageWidth - 20;
    const imgH = (canvas.height * imgW) / canvas.width;
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, imgW, imgH);
    pdf.save(`คำร้องสอบแก้ตัว_${result.student.st_idnumber}_${result.student.st_name}.pdf`);
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">วัดผลและประเมินผล</Link></li>
              <li className="breadcrumb-item active">คำร้องสอบแก้ตัว</li>
            </ol>
            <h4 className="main-title mb-0">คำร้องสอบแก้ตัว</h4>
          </div>
        </div>

        <Row className="g-3 mb-3">
          <Col xl="12">
            <Card className="card-one">
              <Card.Body className="p-3">
                <Form className="d-flex gap-2" onSubmit={e => { e.preventDefault(); handleSearch(); }}>
                  <Form.Control
                    style={{ maxWidth: 260 }}
                    placeholder="กรอกเลขประจำตัวนักเรียน"
                    value={searchId}
                    onChange={e => { setSearchId(e.target.value); setNotFound(false); setResult(null); }}
                  />
                  <Button variant="primary" type="submit" disabled={searching || !searchId.trim()}>
                    {searching ? <Spinner animation="border" size="sm" /> : <><i className="ri-search-line me-1"></i>ค้นหา</>}
                  </Button>
                </Form>
                {notFound && (
                  <div className="text-danger mt-2">
                    <i className="ri-close-circle-line me-1"></i>ไม่พบนักเรียนที่มีเลขประจำตัว {searchId}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {result && (
          <>
            <Card className="card-one mb-3">
              <Card.Body className="p-3">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <StudentAvatar photo={result.student.st_photo} name={result.student.st_name} size={48} />
                  <div>
                    <span className="text-muted fs-sm">เลขประจำตัว</span>
                    <div className="fw-semibold">{result.student.st_idnumber}</div>
                  </div>
                  <div>
                    <span className="text-muted fs-sm">ชื่อ-สกุล</span>
                    <div className="fw-semibold">{result.student.st_name}</div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {failedTerms.length === 0 ? (
              <div className="text-center text-muted py-4">
                <i className="ri-checkbox-circle-line me-1"></i>ไม่พบรายวิชาที่ติด 0 หรือ ร
              </div>
            ) : (
              <Card className="card-one">
                <Card.Body className="p-3">
                  <Row className="g-3 align-items-end mb-3">
                    <Col md="4">
                      <Form.Label className="fw-semibold mb-1">ภาคเรียน/ปีการศึกษาที่ขอสอบแก้ตัว</Form.Label>
                      <Form.Select value={selectedTermKey} onChange={e => setSelectedTermKey(e.target.value)}>
                        {failedTerms.map(t => (
                          <option key={t.key} value={t.key}>
                            {t.c_class} — ภาคเรียนที่ {t.semester} ปีการศึกษา {t.year_year} ({t.rows.length} วิชา)
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md="3">
                      <Form.Label className="fw-semibold mb-1">ภาคเรียนปัจจุบัน</Form.Label>
                      <div className="form-control bg-light" style={{ pointerEvents: 'none' }}>
                        ภาคเรียนที่ {currentTerm.semester} ปีการศึกษา {currentTerm.year_year}
                      </div>
                    </Col>
                    <Col md="5">
                      <Form.Label className="fw-semibold mb-1">ค่าธรรมเนียม</Form.Label>
                      <div className="d-flex align-items-center gap-3">
                        <Badge bg="info" className="fs-sm">
                          ผ่านไป {elapsed} ภาคเรียน → {feePerSubject} บาท/วิชา
                        </Badge>
                        <Form.Check
                          type="checkbox"
                          id="non-subsidy-check"
                          label="ไม่มีชื่อในอุดหนุน (120 บาท/วิชา)"
                          checked={nonSubsidy}
                          onChange={e => setNonSubsidy(e.target.checked)}
                        />
                      </div>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div className="d-flex gap-2">
                      <Button variant="outline-primary" size="sm" onClick={selectAll}>
                        <i className="ri-checkbox-multiple-line me-1"></i>เลือกทั้งหมด
                      </Button>
                      <Button variant="outline-secondary" size="sm" onClick={clearAll}>
                        <i className="ri-checkbox-blank-line me-1"></i>ไม่เลือก
                      </Button>
                    </div>
                    <div className="d-flex gap-2">
                      <Button variant="outline-danger" size="sm" onClick={handleExportPDF} disabled={chosen.length === 0}>
                        <i className="ri-file-pdf-line me-1"></i>ส่งออก PDF
                      </Button>
                      <Button variant="outline-secondary" size="sm" onClick={handlePrint} disabled={chosen.length === 0}>
                        <i className="ri-printer-line me-1"></i>พิมพ์
                      </Button>
                    </div>
                  </div>

                  <Table responsive bordered hover size="sm" className="mb-3">
                    <thead>
                      <tr className="text-center">
                        <th style={{ width: 44 }}>
                          <Form.Check
                            type="checkbox"
                            checked={subjects.length > 0 && chosen.length === subjects.length}
                            onChange={e => (e.target.checked ? selectAll() : clearAll())}
                            title="เลือก/ไม่เลือกทั้งหมด"
                            hidden={true}
                          />
                        </th>
                        <th style={{ width: 80 }}>รหัสวิชา</th>
                        <th>รายวิชา</th>
                        <th style={{ width: 50 }}>นก.</th>
                        <th style={{ width: 90 }}>ผลการสอบเดิม</th>
                        <th style={{ width: 90 }}>ผลการสอบแก้</th>
                        <th style={{ width: 160 }}>ครูผู้สอน</th>
                        <th style={{ width: 110 }}>วัน เดือน ปี</th>
                        <th style={{ width: 110 }}>หมายเหตุ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subjects.map(r => {
                        const k = rowKey(r);
                        const checked = selectedKeys.has(k);
                        return (
                          <tr key={k} className={checked ? '' : 'text-muted'}>
                            <td className="text-center">
                              <Form.Check type="checkbox" checked={checked} onChange={() => toggleKey(k)} />
                            </td>
                            <td className="text-center">{r.sub_subid}</td>
                            <td>
                              {r.sub_subname}
                              {r.sub_type === 'extra' && <span className="text-muted ms-1" style={{ fontSize: '0.8em' }}>(เพิ่มเติม)</span>}
                            </td>
                            <td className="text-center">{formatCredit(r.sub_credit)}</td>
                            <td className="text-center">
                              {originalResult(r)
                                ? <span style={{ color: checked ? 'red' : undefined, fontWeight: 'bold' }}>{originalResult(r)}</span>
                                : <span className="text-muted">—</span>}
                            </td>
                            <td className="text-center text-muted">—</td>
                            <td>{r.teacher_name}</td>
                            <td className="text-center text-muted">—</td>
                            <td className="text-center text-muted">—</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>

                  <div className="d-flex justify-content-between flex-wrap gap-2">
                    <div><strong>รวม</strong> {chosen.length} วิชา <span className="text-muted">(จากทั้งหมด {subjects.length} วิชา)</span></div>
                    <div><strong>รวมเงิน</strong> {totalMoney} บาท ({chosen.length} วิชา × {feePerSubject} บาท)</div>
                  </div>
                  <div className="text-muted fs-sm mt-2">
                    คอลัมน์ "วัน เดือน ปี" และ "หมายเหตุ" เว้นว่างไว้ให้ครูเขียนด้วยมือบนเอกสารที่พิมพ์ออกมา
                  </div>
                </Card.Body>
              </Card>
            )}
          </>
        )}

        <Footer />
      </div>
    </React.Fragment>
  );
}
